import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import * as z from 'zod'

import ExclamationIcon from '@/assets/icons/icon-exclamation-in-triangle.svg'
import { Button } from '@/components/ui/button.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { useAuthenticateTfa } from '@/modules/profile-settings/hooks/useAuthenticateTfa.ts'
import { emailSchema } from '@/types/schemas.ts'
import { getErrorMessage } from '@/utils/error'

const tfaAuthenticateFormSchema = z.object({
  email: emailSchema,
  token: z.string(),
  code: z.string(),
})

type TfaAuthenticateFormValues = z.infer<typeof tfaAuthenticateFormSchema>

interface AccountTfaAuthenticateFormProps {
  email: string
  token?: string
  isSetupRequired: boolean
  onFinish?: () => void
}

export const AccountTfaAuthenticateForm = ({
  email,
  token,
  isSetupRequired,
  onFinish,
}: AccountTfaAuthenticateFormProps) => {
  const account = useAccount()
  const form = useForm<TfaAuthenticateFormValues>({
    mode: 'onChange',
    resolver: zodResolver(tfaAuthenticateFormSchema),
    defaultValues: {
      token: token || account.accessToken!,
      email,
    },
  })
  const authenticateTfa = useAuthenticateTfa()

  const {
    handleSubmit,
    formState: { errors },
  } = form

  const onSubmit = async (values: TfaAuthenticateFormValues) => {
    try {
      const response = await authenticateTfa.mutateAsync(values)

      if (response?.isRecoveryCodeUsed) {
        form.setError('code', {
          type: 'manual',
          message:
            "Hey, this recovery code was used. Please make sure to disable your 2FA if you don't have access to the device anymore.",
        })
      } else {
        onFinish?.()
      }
    } catch (error) {
      form.setError('code', {
        type: 'manual',
        message: getErrorMessage(error),
      })
    }
  }

  return (
    <div>
      {isSetupRequired && (
        <div className="flex items-start mb-4 bg-orange-100 dark:bg-orange-900 py-3 px-4 rounded-xl">
          <ExclamationIcon className="mr-3 mt-1 w-6 h-6 flex-shrink-0 text-orange-500" />
          <div className="flex flex-col">
            <p className="text-text-default  text-base font-medium">
              Important Security Update
            </p>
            <p className="text-sm text-text-sub">
              To protect your account please activate 2FA to continue.
              We&apos;ve sent an email to your login address with instructions.
              Check your latest email from Creatorhero to securely access your
              account.
            </p>
          </div>
        </div>
      )}
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {errors.root && (
            <div className="mb-4 w-full rounded-md bg-danger-light py-1.5 text-center text-sm text-danger-default">
              {errors.root?.message}
            </div>
          )}

          <div className="mb-6 flex flex-col gap-6">
            <FormField
              name="code"
              label="Two Factor Authentication Code"
              placeholder="Enter Authentication Code"
            />
          </div>

          <Button className="w-full mb-3">Authenticate</Button>
        </form>
      </FormProvider>
    </div>
  )
}
